import { ApolloProvider } from '@apollo/client';
import { ThemeProvider } from '@mui/material/styles';
import ReportsContainer from 'containers/reports';
import PropTypes from 'prop-types';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { syncHistoryWithStore } from 'react-router-redux';
import { ToastContainer } from 'react-toastify';
import { MIXPANEL_EVENT_PAGE_LOADED } from 'utils/consts';
import { formatSectionPaths, isPathAllowed, PATHS_NOT_IN_NAVIGATION_MENU, trackMixpanelEvent } from 'utils/general';
import client from './apollo';
import Main from './components/Main';
import * as Pages from './components/pages';
import Spinner from './components/widgets/Spinner';
import pageConfigs from './config';
import * as Containers from './containers';
import theme from './global/muiTheme';
import browserHistory from './hashHistory';
import reportsConfig from './reducers/reportsConfig';
import { getNavigationSections } from './selectors/navigation';

const CREATIVE_IQ_PATH = '/react/creative-iq';
const CREATIVE_GALLERY_PATH = '/react/creative-gallery';
const CREATIVE_EXPLORE_PATH = '/react/creative-explore';
const CREATIVE_DASHBOARD_PATH = '/react/creative-dashboard';
const STORAGE_INITIAL_ROUTE_PATH = 'initial_route_path';

const CREATIVE_IQ_ROUTES = new Set([CREATIVE_GALLERY_PATH, CREATIVE_EXPLORE_PATH, CREATIVE_DASHBOARD_PATH]);

const basicRoutes = pageConfigs
    .filter(pageConfig => pageConfig.container || pageConfig.componentName || pageConfig.containerName)
    .map(pageConfig => {
        const component =
            pageConfig.container ||
            (pageConfig.componentName ? Pages[pageConfig.componentName] : Containers[pageConfig.containerName]);
        if (!component) {
            throw new Error(`undefined component: ${pageConfig.componentName || pageConfig.containerName}`);
        }
        return {
            type: pageConfig.type || '',
            path: pageConfig.path,
            mixpanelComponentName: pageConfig.mixpanelComponentName,
            component,
        };
    })
    .concat(
        reportsConfig.map(({ type, path, className }) => {
            return {
                type,
                path,
                component: Containers[className] || ReportsContainer,
            };
        })
    )
    .concat(
        pageConfigs
            .filter(pageConfig => pageConfig.lazy)
            .map(pageConfig => {
                return {
                    type: pageConfig.type || '',
                    path: pageConfig.path,
                    component: pageConfig.lazy,
                };
            })
    );

function SingularRouter({ store = {} }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [routes, setRoutes] = useState(basicRoutes);
    const shouldHideSidenav = useMemo(() => {
        const hideSidenavPaths = ['signup', 'anonymous', 'loading_page', 'plan-upgrade'];

        return hideSidenavPaths.some(path => location.pathname.includes(path)) || window.hideSideBar;
    }, [location]);

    const sections = useSelector(getNavigationSections);

    /**
     * Filter routes based on the sections fetched from the API.
     * Helps to avoid flickering when a user is trying to visit a non-allowed page.
     */
    useEffect(() => {
        const sessionRoute = sessionStorage.getItem(STORAGE_INITIAL_ROUTE_PATH);

        if (!sections) {
            const currentPath = location.pathname;
            !sessionRoute && sessionStorage.setItem(STORAGE_INITIAL_ROUTE_PATH, currentPath);
            return;
        } else {
            sessionStorage.removeItem(STORAGE_INITIAL_ROUTE_PATH);
        }

        const formattedSections = formatSectionPaths(sections).concat(PATHS_NOT_IN_NAVIGATION_MENU);

        // Check permissions before filtering routes
        const isCreativeGalleryAllowed = isPathAllowed(formattedSections, CREATIVE_GALLERY_PATH);
        const isCreativeIQAllowed = isPathAllowed(formattedSections, CREATIVE_IQ_PATH);

        const currentPath = sessionRoute || location.pathname;

        // Redirect to Creative IQ if user tries to access one of the Creative IQ pages without permission.
        if (isCreativeIQAllowed) {
            if (CREATIVE_IQ_ROUTES.has(currentPath) && !isPathAllowed(formattedSections, currentPath)) {
                navigate(CREATIVE_IQ_PATH);
                return;
            }
        }
        // In case the path is CREATIVE_IQ_PATH (trial landing page) - go to gallery as default page if allowed.
        else if (currentPath === CREATIVE_IQ_PATH && isCreativeGalleryAllowed) {
            navigate(CREATIVE_GALLERY_PATH);
            return;
        }

        const filteredRoutes = basicRoutes.filter(({ path }) => {
            return isPathAllowed(formattedSections, path);
        });

        setRoutes(filteredRoutes);
    }, [sections, location.pathname]);

    useEffect(() => {
        const history = syncHistoryWithStore(browserHistory, store);
        const path = `${location.pathname}/${location.search}`;

        history.replace(path);

        routes.forEach(({ component, mixpanelComponentName, path: componentPath }) => {
            if (component.locationHandler) {
                browserHistory.listen(component.locationHandler);
            }
            if (mixpanelComponentName) {
                browserHistory.listen(({ pathname }) => {
                    if (pathname === componentPath) {
                        trackMixpanelEvent(mixpanelComponentName, MIXPANEL_EVENT_PAGE_LOADED);
                    }
                });
            }
        });
    }, []);

    return (
        <ApolloProvider client={client}>
            <ThemeProvider theme={theme}>
                <>
                    <Main sections={sections} shouldHideSidenav={shouldHideSidenav}>
                        <Suspense fallback={<Spinner style={{ marginTop: '300px' }} show />}>
                            <Routes>
                                {routes.map(({ type, path, component }) => (
                                    <Route
                                        key={`route_${type}`}
                                        path={path}
                                        element={React.createElement(component, { type })}
                                    />
                                ))}
                            </Routes>
                        </Suspense>
                    </Main>
                    <ToastContainer />
                </>
            </ThemeProvider>
        </ApolloProvider>
    );
}

SingularRouter.propTypes = {
    store: PropTypes.objectOf(PropTypes.any),
};

export default SingularRouter;
